import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import messages from '../messages'
import StepHeading from '../StepHeading'
import ChooseSubscription from 'js/components/SignUp/ChooseSubscription/ChooseSubscription'
import ChooseSubscriptionModal from 'js/components/SignUp/ChooseSubscription/ChooseSubscriptionModal'
import {
  selectCoupon,
  selectCouponError,
  selectPricingConfig,
} from 'js/store/pricingSlice'
import { useSelector } from 'react-redux'
import Spinner from 'js/components/Spinner/Spinner'
import CurrencySwitcher from 'js/components/CurrencySwitcher/CurrencySwitcher'
import CogIcon from 'media/cog.svg?svgr-webpack'
import Button from 'js/components/Button/Button'

import DiscountIcon from 'media/discount.svg?svgr-webpack'
import { trackSelectPremiumSubscription } from 'js/analytics'
import { pushToDataLayer } from 'js/pages/LandingPages/utils'

function SignUpChoosePlan({ onComplete, hideCoupon }) {
  const [showCouponModal, setShowCouponModal] = useState()

  const selectedCoupon = useSelector(selectCoupon)
  const isLoading = useSelector((state) => state.pricing.isLoading)
  const { code } = useParams()
  const couponError = useSelector(selectCouponError(code))
  const pricingConfig = useSelector(selectPricingConfig(selectedCoupon))

  function stepComplete(planDuration) {
    typeof hj !== 'undefined' && hj('trigger', 'enter_payment_information')
    trackSelectPremiumSubscription(
      planDuration,
      pricingConfig.discount,
      pricingConfig.coupon,
    )
    pushToDataLayer({
      event: 'signup_form',
      section: 'select_subscription',
      label: planDuration, //headline of selected goal
      success: '1', //send 1 if the goal selection is successfull and the user proceeds to the next step successfully, 0 if any error occurs
      error_message: undefined, //if an error occurs when the user selects a goal and proceeds to the next step send error messages, if no error send undefined
    })
    if (onComplete) onComplete(planDuration)
  }

  return (
    <div className="relative w-full">
      {!showCouponModal && isLoading && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          style={{ background: '#F4F3F0' }}
        >
          <div className="w-64 text-green">
            <Spinner color="green" />
          </div>
        </div>
      )}
      {couponError && code && (
        <div
          className="relative mx-auto mb-40 max-w-768 rounded-8 border-2 border-secondary-red bg-white px-12 py-24"
          data-uid="error_message"
        >
          {code && (
            <span className="absolute left-1/2 top-0 flex -translate-x-1/2  -translate-y-1/2 transform items-center rounded-full bg-secondary-red px-12 py-2 leading-default text-white">
              <DiscountIcon className="mb-2 mr-6" /> {code}
            </span>
          )}
          <p className="mt-16 font-demibold text-secondary-red">
            {couponError.errorDetail.includes('expired') ? (
              <FormattedMessage
                defaultMessage="We’re sorry, the code has expired."
                id="Redeem.start.expiredMessage"
              />
            ) : (
              <FormattedMessage
                id="Signup.invalidCode"
                defaultMessage="Sorry, the code is invalid."
              />
            )}
          </p>
          <Button
            color="black"
            outline
            onClick={() => setShowCouponModal(true)}
            className="mb-6 mt-16"
            nudge={false}
          >
            <FormattedMessage
              id="manage_coupon_change"
              defaultMessage="Change code"
            />
          </Button>
        </div>
      )}
      <StepHeading>
        <FormattedMessage {...messages.chooseAPlan} />
      </StepHeading>
      <div className="mt-36 w-full">
        <div className="mx-6 -mb-20 flex h-16 items-center justify-between lg:justify-start">
          <span className="mr-24 px-16">
            <CurrencySwitcher />
          </span>
          {!showCouponModal && !hideCoupon && (
            <button
              className="flex items-center text-LSBrand underline transition type-14 hover:text-Type"
              onClick={() => setShowCouponModal(true)}
            >
              {selectedCoupon ? (
                <span className="flex">
                  <span className="mr-4 text-TypeSub">
                    <FormattedMessage
                      id="manage_coupon_code"
                      defaultMessage="Code:"
                    />
                  </span>
                  <span className="type-14-caps">
                    {selectedCoupon}
                    <span className="sr-only">
                      <FormattedMessage
                        id="manage_coupon_change"
                        defaultMessage="Change code"
                      />
                    </span>
                  </span>
                </span>
              ) : (
                <FormattedMessage
                  id="manage_coupon_heading"
                  defaultMessage="Have a discount code?"
                />
              )}
              {selectedCoupon && (
                <span className="ml-6 mt-2">
                  <CogIcon />
                </span>
              )}
            </button>
          )}
        </div>
        <ChooseSubscription onSelectSubscription={stepComplete} />
        <div className="mx-6 mb-36 justify-between text-17 lg:flex">
          {selectedCoupon && (
            <span className="text-TypeSub lg:text-left">
              <FormattedMessage
                id="upgrade.standard_renewal_notice"
                defaultMessage="Standard renewal price applies"
              />
            </span>
          )}
          <p className="mt-20 text-center lg:ml-auto lg:text-left">
            <span className="mx-24 block lg:mx-0 lg:inline-block">
              <FormattedMessage {...messages['cancel']} />
            </span>
            <picture>
              <source
                srcSet={`${require('media/payment-methods.png')}, ${require('media/payment-methods@2x.png')} 2x`}
              />
              <img
                className="mt-12 lg:ml-16 lg:mt-0"
                src={require('media/payment-methods.png')}
                alt="We accept Visa, MasterCard, American Express and PayPal"
                height="21"
                width="180"
              />
            </picture>
          </p>
        </div>
      </div>
      {showCouponModal && (
        <ChooseSubscriptionModal
          onClose={() => setShowCouponModal(false)}
          error={couponError}
        />
      )}
    </div>
  )
}

SignUpChoosePlan.propTypes = {
  pricingConfig: PropTypes.shape({
    coupon: PropTypes.string,
    products: PropTypes.array,
  }),
  onComplete: PropTypes.func,
  planDuration: PropTypes.number,
  onBack: PropTypes.func,
  onChange: PropTypes.func,
  hideCoupon: PropTypes.bool,
}

export default SignUpChoosePlan
